import { Image } from '@smartgift/frontend-toolkit';
import styled, { css } from 'styled-components';

import { DEVICE } from '@hero/constants';

const CardHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 8px;
  text-transform: capitalize;
`;

const RecipientCharity = styled.div`
  display: flex;
  gap: 40px;
  line-height: 150%;
`;

const RecipientCharityImage = styled.div`
  height: fit-content;
  position: relative;
`;

const RecipientCharityImageSource = styled(Image)`
  border-radius: var(--size-border-radius-md);
`;

const RecipientCharityBody = styled.div``;

const RecipientCharityTitle = styled.div`
  color: var(--color-accent-secondary-default);
  font-weight: 700;
`;

const RecipientCharityDescription = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  display: -webkit-box;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  align-items: center;
  background-color: var(--color-bg-body);
  border: 2px solid var(--color-bg-weaker2);
  border-radius: var(--size-border-radius-circle);
  display: flex;
  height: 30px;
  justify-content: center;
  left: 4px;
  position: absolute;
  top: 4px;
  width: 30px;
`;

const TextPlaceholder = styled.div`
  color: var(--color-neutral-400);
  font-style: italic;
`;

const TableHeaderButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-left: 24px;
`;

const RecipientDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .item-body {
    align-items: center;
    padding: 0;
    min-height: 56px;
  }

  .item-content {
    padding: 16px 0;
  }

  .detail-group {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    .item-body {
      &.regifted {
        .item-content {
          color: var(--color-accent-primary-default);
          text-transform: uppercase;
        }
      }

      &.non-empty {
        .item-content {
          font-weight: 700;
          text-align: end;
        }
      }
    }
  }
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  ${DEVICE.desktop.min} {
    flex-direction: column;
  }
`;

const Box = styled.div`
  background: var(--color-bg-body);
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  display: ${({ isRight }) => (isRight ? 'flex' : 'block')};
  height: 100%;
  padding: ${({ isRight }) => (isRight ? 0 : '24px')};
`;

const ContactInfo = styled.div`
  flex: 1;

  ${DEVICE.tablet.max} {
    margin-right: 8px;
  }
`;

const GiftDetail = styled.div`
  flex: 1;
`;

const GiftStatusGroup = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const GiftStatus = styled.div`
  flex: 1;
  padding: 24px;
`;

const ProductWrapper = styled.div`
  align-items: ${({ isNonEmpty }) => (isNonEmpty ? 'flex-start' : 'center')};
  flex-direction: ${({ isNonEmpty }) => (isNonEmpty ? 'column' : 'row')};
  word-break: break-word;
`;

const ProductCard = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;

  ${Image?.S?.Image} {
    border-radius: var(--radius-m);
  }
`;

const ProductDetail = styled.div`
  width: 200px;
`;

const ProductName = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Dot = styled.div`
  background-color: var(--color-gray-25);
  border: 16px solid var(--color-gray-25);
  border-radius: 100%;
  margin-left: 15px;
  position: relative;
  z-index: 1;

  &:after {
    background-color: var(--color-gray-25);
    border: 3px solid var(--color-gray-400);
    border-radius: 100%;
    content: ' ';
    height: 6px;
    left: 50%;
    position: absolute;
    top: -8px;
    transform: translate(-50%, 0);
    width: 6px;

    ${({ isSelected }) =>
      isSelected &&
      css`
        background-color: var(--color-neutral-800);
        border: 3px solid var(--color-neutral-800);
        height: 8px;
        width: 8px;
      `}
  }
`;

const Steps = styled.div`
  margin-bottom: 24px;
  margin-top: 32px;
  position: relative;

  &::after {
    border-color: var(--color-navy-50);
    border-right: 1px dashed var(--color-gray-400);
    bottom: 26px;
    content: '';
    left: 30px;
    position: absolute;
    top: 0;
  }

  ${DEVICE.mobile.max} {
    &::after {
      left: 32px;
    }
  }
`;

const StepsContainer = styled.div`
  background-color: inherit;
  display: flex;
  margin-top: 60px;
  position: relative;

  &:first-child {
    margin-top: 0;
  }

  ${DEVICE.mobile.max} {
    flex-direction: row-reverse;
    left: 0;
    margin-bottom: 16px;
    width: auto;
  }
`;

const StepContent = styled.div``;

const StepDate = styled.div`
  display: flex;
  margin-top: 8px;

  &:before {
    background-color: var(--color-neutral-800);
    border-radius: 0 var(--radius-m) var(--radius-m) 0;
    content: '';
    height: 1px;
    margin-left: -17px;
    margin-top: 9px;
    position: absolute;
    width: 9px;
  }
`;

const TimeLine = styled.div`
  background-color: var(--color-bg-weaker);
  margin-bottom: 6px;
  margin-right: 6px;
  margin-top: 6px;
  width: 188px;
`;

export {
  Box,
  CardHeader,
  ContactInfo,
  Dot,
  GiftDetail,
  GiftStatus,
  GiftStatusGroup,
  IconWrapper,
  ProductCard,
  ProductDetail,
  ProductName,
  ProductWrapper,
  RecipientCharity,
  RecipientCharityBody,
  RecipientCharityDescription,
  RecipientCharityImage,
  RecipientCharityImageSource,
  RecipientCharityTitle,
  RecipientDetailWrapper,
  SectionWrapper,
  StepContent,
  StepDate,
  Steps,
  StepsContainer,
  TableHeaderButtonWrapper,
  TextPlaceholder,
  TimeLine
};
